import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Label,
} from "recharts";

const PALETTE = [
  "#1a1a2e", "#6c63ff", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#06b6d4", "#f97316",
];

function shorten(str, max = 13) {
  const s = String(str ?? "");
  return s.length > max ? s.slice(0, max) + "…" : s;
}

function buildRows(chart) {
  const { labels = [], datasets = [] } = chart;
  if (!labels.length || !datasets.length) return [];
  return labels.map((lbl, i) => {
    const row = { label: lbl };
    datasets.forEach((ds) => { row[ds.label] = ds.data[i] ?? 0; });
    return row;
  });
}

// Infer human-readable axis label from chart metadata
function inferAxisLabels(chart) {
  const meta = chart.query_metadata || {};
  const groupBy = meta.group_by;
  const metric  = meta.metric || "";

  const xLabels = {
    day: "Date", week: "Week", month: "Month", year: "Year",
    country: "Country", location: "Location / Office", seller: "Salesperson",
    shape: "Shape", lab: "Lab", status: "Status", block_type: "Hold Type",
    account: "Client", user_type: "User Type", color: "Color", clarity: "Clarity", cut: "Cut",
  };

  const yLabels = {
    total_sales: "Orders (count)", total_revenue: "Revenue (USD)",
    avg_order_value: "Avg Order Value (USD)", hold_count: "Holds",
    hold_revenue: "Hold Value (USD)", inquiry_count: "Enquiries",
    cart_count: "Cart Additions",
    new_users: "New Users", new_accounts: "New Accounts",
    total_carats_sold: "Carats Sold", inventory_count: "Stones",
    inventory_value: "Inventory Value (USD)", memo_count: "Memos",
    top_salespersons: "Revenue (USD)", top_clients: "Orders",
    top_shapes: "Sales Count",
    stones_sold: "Stones Sold",
  };

  return {
    x: xLabels[groupBy] || (groupBy ? groupBy : "Period"),
    y: yLabels[metric] || "Value",
  };
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-lg text-xs max-w-xs">
      <p className="font-semibold text-gray-700 mb-1 truncate">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <strong>{typeof p.value === "number" ? p.value.toLocaleString() : p.value}</strong>
        </p>
      ))}
    </div>
  );
};

// ── Line Chart ─────────────────────────────────────────────────────────────────
function LineChartView({ chart }) {
  const rows = buildRows(chart);
  if (!rows.length) return <Empty />;
  const keys = chart.datasets.map((d) => d.label);
  const forecastKeys = new Set(chart.datasets.filter((d) => d.is_forecast).map((d) => d.label));
  const { x: xLabel, y: yLabel } = inferAxisLabels(chart);

  return (
    <ResponsiveContainer width="100%" height={280}>
      <LineChart data={rows} margin={{ top: 10, right: 20, bottom: 40, left: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }}
               tickFormatter={(v) => shorten(v, 9)} angle={-30} textAnchor="end" height={55}>
          <Label value={xLabel} offset={-8} position="insideBottom" style={{ fontSize: 11, fill: "#94a3b8" }} />
        </XAxis>
        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={55}>
          <Label value={yLabel} angle={-90} position="insideLeft" offset={15}
                 style={{ fontSize: 11, fill: "#94a3b8", textAnchor: "middle" }} />
        </YAxis>
        <Tooltip content={<CustomTooltip />} />
        {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />}
        {keys.map((key, i) => (
          <Line key={key} type="monotone" dataKey={key}
                stroke={PALETTE[i % PALETTE.length]} strokeWidth={forecastKeys.has(key) ? 1.5 : 2.5}
                strokeDasharray={forecastKeys.has(key) ? "6 4" : undefined}
                dot={rows.length <= 24 && !forecastKeys.has(key) ? { r: 3 } : false}
                activeDot={{ r: 5 }} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Bar Chart ──────────────────────────────────────────────────────────────────
function BarChartView({ chart }) {
  const rows = buildRows(chart);
  if (!rows.length) return <Empty />;
  const keys = chart.datasets.map((d) => d.label);
  const { x: xLabel, y: yLabel } = inferAxisLabels(chart);

  const maxLabelLen = Math.max(...(chart.labels || []).map((l) => String(l).length));
  const isHorizontal = rows.length > 7 || maxLabelLen > 12;

  if (isHorizontal) {
    return (
      <ResponsiveContainer width="100%" height={Math.max(260, rows.length * 42)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 60, bottom: 4, left: 120 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }}>
            <Label value={yLabel} position="insideBottom" offset={-5}
                   style={{ fontSize: 11, fill: "#94a3b8" }} />
          </XAxis>
          <YAxis dataKey="label" type="category" width={116}
                 tick={{ fontSize: 10, fill: "#64748b" }} tickFormatter={(v) => shorten(v, 16)}>
            <Label value={xLabel} angle={-90} position="insideLeft" offset={20}
                   style={{ fontSize: 11, fill: "#94a3b8", textAnchor: "middle" }} />
          </YAxis>
          <Tooltip content={<CustomTooltip />} />
          {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11 }} />}
          {keys.map((key, i) => (
            <Bar key={key} dataKey={key} fill={PALETTE[i % PALETTE.length]} radius={[0, 4, 4, 0]} maxBarSize={28} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={rows} margin={{ top: 10, right: 20, bottom: 40, left: 60 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }}
               tickFormatter={(v) => shorten(v, 10)} angle={-30} textAnchor="end" height={55}>
          <Label value={xLabel} offset={-8} position="insideBottom"
                 style={{ fontSize: 11, fill: "#94a3b8" }} />
        </XAxis>
        <YAxis tick={{ fontSize: 10, fill: "#64748b" }} width={55}>
          <Label value={yLabel} angle={-90} position="insideLeft" offset={15}
                 style={{ fontSize: 11, fill: "#94a3b8", textAnchor: "middle" }} />
        </YAxis>
        <Tooltip content={<CustomTooltip />} />
        {keys.length > 1 && <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />}
        {keys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={PALETTE[i % PALETTE.length]} radius={[4, 4, 0, 0]} maxBarSize={50} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Pie Chart ──────────────────────────────────────────────────────────────────
function PieChartView({ chart }) {
  const { labels = [], datasets = [] } = chart;
  if (!labels.length || !datasets.length) return <Empty />;
  const pieData = labels.map((name, i) => ({ name, value: datasets[0]?.data[i] ?? 0 }));
  const total = pieData.reduce((s, d) => s + d.value, 0);
  return (
    <ResponsiveContainer width="100%" height={270}>
      <PieChart>
        <Pie data={pieData} dataKey="value" nameKey="name" cx="45%" cy="50%"
             outerRadius={100} innerRadius={42}
             label={({ name, percent }) =>
               percent > 0.04 ? `${shorten(name, 8)} ${(percent * 100).toFixed(0)}%` : ""}
             labelLine={false}>
          {pieData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
        </Pie>
        <Tooltip formatter={(val) => [`${val.toLocaleString()} (${((val / total) * 100).toFixed(1)}%)`, ""]} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

// ── Stat Cards ─────────────────────────────────────────────────────────────────
function StatCardsView({ chart }) {
  const cards = chart.kpi_cards || [];
  if (!cards.length) return <Empty />;
  const cols = cards.length >= 3 ? "grid-cols-3" : cards.length === 2 ? "grid-cols-2" : "grid-cols-1";
  return (
    <div className={`grid gap-3 ${cols}`}>
      {cards.map((c, i) => (
        <div key={i} className="rounded-xl border border-gray-200 bg-gradient-to-br from-gray-50 to-white p-4 shadow-sm">
          <p className="text-xs text-gray-500 mb-1 truncate">{c.label}</p>
          <p className="text-2xl font-bold text-gray-900 leading-tight">{c.value}</p>
          {c.change && (
            <p className={`text-xs mt-1 font-medium ${
              c.trend === "up" ? "text-emerald-600" : c.trend === "down" ? "text-red-500" : "text-gray-400"
            }`}>{c.trend === "up" ? "↑" : c.trend === "down" ? "↓" : ""} {c.change}</p>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Forecast Info Banner ───────────────────────────────────────────────────────
function ForecastBanner({ forecast }) {
  if (!forecast?.confidence) return null;
  const pct = Math.round(forecast.confidence * 100);
  const warning = forecast.warning;
  return (
    <div className={`mt-2 px-3 py-2 rounded-lg text-xs flex gap-2 items-start ${
      warning ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-blue-50 text-blue-700 border border-blue-100"
    }`}>
      <span>{warning ? "⚠️" : "📈"}</span>
      <span>
        {warning ? warning : `Forecast confidence: ${pct}% · Method: ${forecast.method || "auto"}`}
      </span>
    </div>
  );
}

// ── KPI Strip ──────────────────────────────────────────────────────────────────
function KPIStrip({ cards }) {
  if (!cards?.length) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {cards.map((c, i) => (
        <div key={i} className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 text-sm flex gap-2 items-center">
          <span className="text-gray-400 text-xs">{c.label}</span>
          <span className="font-semibold text-gray-900">{c.value}</span>
          {c.change && (
            <span className={`text-xs font-medium ${
              c.trend === "up" ? "text-emerald-600" : c.trend === "down" ? "text-red-500" : "text-gray-400"
            }`}>{c.change}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Comparison Table ───────────────────────────────────────────────────────────
function ComparisonTable({ table }) {
  if (!table?.length) return null;
  const headers = Object.keys(table[0]);
  return (
    <div className="mt-4 overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50">
            {headers.map((h) => (
              <th key={h} className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide border-b border-gray-200">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              {headers.map((h) => (
                <td key={h} className="px-4 py-2 text-gray-800 border-b border-gray-100">{row[h]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty() {
  return (
    <div className="flex items-center justify-center h-20 text-gray-400 text-sm italic">
      No data available for this period
    </div>
  );
}

// ── Main Export ────────────────────────────────────────────────────────────────
export default function ChartRenderer({ chart }) {
  if (!chart) return null;
  const { chart_type, kpi_cards, comparison_table, forecast } = chart;
  const showKpiStrip = chart_type !== "stat_cards" && kpi_cards?.length > 0;

  return (
    <div className="mt-2">
      {chart_type === "line"       && <LineChartView chart={chart} />}
      {chart_type === "bar"        && <BarChartView chart={chart} />}
      {chart_type === "pie"        && <PieChartView chart={chart} />}
      {chart_type === "stat_cards" && <StatCardsView chart={chart} />}
      {!["line","bar","pie","stat_cards"].includes(chart_type) && chart.labels?.length > 0 && <LineChartView chart={chart} />}

      {forecast && <ForecastBanner forecast={forecast} />}
      {showKpiStrip && <KPIStrip cards={kpi_cards} />}
      <ComparisonTable table={comparison_table} />
    </div>
  );
}